import { RETIRED_GREETING_CARD_MESSAGE } from '@giftie/shared/greeting-cards'
import type { CompanyShopperState } from './useYuzuBuilder'
import {
  composeYuzuTemplateSchemas,
  type YuzuTemplateSchema,
} from '@giftie/yuzu-integration'
import { resolvePageEdgeSafeAreaMm } from '~/utils/pageEdgeSafeArea'
import {
  applyYuzuFontManifest,
  applyYuzuCompanyFonts,
} from '~/utils/yuzuFontFaces'

type YuzuHostInitPayload = {
  companyShopper?: CompanyShopperState
  mode?: string
  yuzuGcbEnabled?: boolean
  shop?: string
  shopifyVariantId?: string
  templateId?: string
  templateVersionId?: string
  templateSchema?: YuzuTemplateSchema
  designSchema?: YuzuTemplateSchema
  baseTemplateSchema?: YuzuTemplateSchema | null
  sessionId?: string
  preserveState?: boolean
  requestId?: number
  cardFormat?: string
  safeAreaMm?: number
  playbackBaseUrl?: string
}

type YuzuHostAssetsPayload = {
  assetUrls?: unknown
}

type ResolvedYuzuTemplate = {
  success: true
  templateId: string
  templateVersionId: string
  schema: YuzuTemplateSchema
  companyShopper?: CompanyShopperState
}

type RestoredYuzuSession = {
  success: true
  session: {
    id: string
    templateId: string
    templateVersionId: string
    shopifyVariantId: string
    templateSchema: YuzuTemplateSchema
    payload: YuzuTemplateSchema
    companyShopper?: CompanyShopperState
  }
}

/** Registers the single trusted host-message listener used by v2 routes. */
export function useYuzuHostBridge() {
  const route = useRoute()
  const router = useRouter()
  const { post } = useEmbed()
  const { captureOpened } = useGreetingCardSessionTracking()
  const { isTrustedOrigin } = useParentMessage()
  const { initialize, initializationError } = useYuzuBuilder()
  const { setAssetUrls } = useYuzuAssetUrls()
  const { setPlaybackBaseUrl } = useBuilder()
  const applyingRequest = useState<number | null>(
    'yuzu-builder:applying-request',
    () => null,
  )

  async function preloadBuilderRoutes() {
    const routes = [
      '/v2/design/preload/front',
      '/v2/design/preload/inside',
      '/v2/design/preload/review',
    ]
    await Promise.all(
      routes.map((path) =>
        preloadRouteComponents({ path, query: route.query }),
      ),
    )
  }

  function announceReady() {
    post('gm/ready', { runtime: 'yuzu-v2' })
    void preloadBuilderRoutes().catch((error) => {
      console.warn('[GiftieGCB] Failed to preload Yuzu builder routes', error)
    })
  }

  /** Uses the inline schema when available, otherwise loads it from Shopify. */
  async function resolveSchema(payload: YuzuHostInitPayload) {
    const designSchema = payload.designSchema ?? payload.templateSchema
    // Company catalogs already compose the corporate template and print base.
    if (designSchema && payload.companyShopper) {
      return {
        schema: designSchema,
        templateVersionId: payload.templateVersionId,
        companyShopper: payload.companyShopper,
      }
    }
    if (designSchema && payload.baseTemplateSchema) {
      return {
        schema: composeYuzuTemplateSchemas(
          payload.baseTemplateSchema,
          designSchema,
          {
            cardFormat: payload.cardFormat === 'flat' ? 'flat' : 'folded',
          },
        ),
        templateVersionId: payload.templateVersionId,
        companyShopper: payload.companyShopper,
      }
    }
    if (designSchema && typeof designSchema.baseTemplateId !== 'string') {
      return {
        schema: designSchema,
        templateVersionId: payload.templateVersionId,
        companyShopper: payload.companyShopper,
      }
    }

    const query = new URLSearchParams({
      shop: payload.shop!,
      shopifyVariantId: String(payload.shopifyVariantId),
      templateId: payload.templateId!,
      templateVersionId: payload.templateVersionId!,
    })
    const response = await $fetch<ResolvedYuzuTemplate>(
      `/api/greeting-card/yuzu/template?${query.toString()}`,
    )
    return response
  }

  /** Loads persisted v2 state through the policy-validating restore endpoint. */
  async function loadSession(payload: YuzuHostInitPayload) {
    if (!payload.preserveState || !payload.sessionId || !payload.shop)
      return null
    const query = new URLSearchParams({ shop: payload.shop })
    const response = await $fetch<RestoredYuzuSession>(
      `/api/v2/greeting-card/session/${encodeURIComponent(payload.sessionId)}?${query.toString()}`,
    )
    return response.session
  }

  /** Applies one valid Yuzu initialization and acknowledges it after navigation. */
  async function applyInit(payload: YuzuHostInitPayload) {
    const requestId = payload.requestId ?? 0
    if (applyingRequest.value === requestId) return
    applyingRequest.value = requestId

    try {
      if (payload.yuzuGcbEnabled !== true)
        throw new Error('The v2 builder requires yuzuGcbEnabled')
      if (
        !payload.shop ||
        !payload.shopifyVariantId ||
        !payload.templateId ||
        !payload.templateVersionId
      ) {
        throw new Error('The Yuzu initialization identity is incomplete')
      }

      const restoredSession = await loadSession(payload)
      if (
        restoredSession &&
        (restoredSession.templateId !== payload.templateId ||
          restoredSession.shopifyVariantId !== String(payload.shopifyVariantId))
      ) {
        throw new Error(
          'The saved Yuzu session does not match this Shopify variant',
        )
      }
      const resolved = restoredSession ? null : await resolveSchema(payload)
      const sourceSchema = restoredSession?.templateSchema ?? resolved!.schema
      const companyShopper =
        restoredSession?.companyShopper ?? resolved?.companyShopper
      const templateVersionId =
        restoredSession?.templateVersionId ??
        resolved?.templateVersionId ??
        payload.templateVersionId
      const schema = restoredSession?.payload ?? sourceSchema
      await applyYuzuFontManifest(schema.fontManifest)
      await applyYuzuCompanyFonts(companyShopper)
      initialize({
        companyShopper,
        templateId: payload.templateId,
        templateVersionId,
        shop: payload.shop,
        shopifyVariantId: String(payload.shopifyVariantId),
        sessionId: restoredSession?.id ?? payload.sessionId,
        cardFormat: payload.cardFormat === 'flat' ? 'flat' : 'folded',
        safeAreaMm: resolvePageEdgeSafeAreaMm(payload.safeAreaMm),
        restoredFromSession: Boolean(restoredSession),
        schema,
        sourceSchema,
      })
      const sessionId = restoredSession?.id ?? payload.sessionId
      if (sessionId) {
        captureOpened({
          sessionId,
          shop: payload.shop,
          source: 'yuzu_iframe',
          templateId: payload.templateId,
        })
      }
      if (payload.playbackBaseUrl) setPlaybackBaseUrl(payload.playbackBaseUrl)
      await router.replace({
        path: `/v2/design/${payload.templateId}/front`,
        query: route.query,
      })
      await nextTick()
      post('gm/initApplied', {
        requestId: payload.requestId ?? null,
        templateId: payload.templateId,
        templateVersionId,
      })
    } catch (error) {
      // SAFETY: Fetch errors expose an HTTP status; other thrown values use the ordinary fallback.
      const message =
        (error as { statusCode?: number })?.statusCode === 410
          ? RETIRED_GREETING_CARD_MESSAGE
          : error instanceof Error
            ? error.message
            : 'Unable to initialize Yuzu template'
      initializationError.value = message
      post('gm/initFailed', {
        requestId: payload.requestId ?? null,
        templateId: payload.templateId ?? null,
        templateVersionId: payload.templateVersionId ?? null,
        message,
      })
    } finally {
      applyingRequest.value = null
    }
  }

  /** Accepts only trusted, shaped v2 host messages. */
  function handleWindowMessage(event: MessageEvent) {
    if (
      event.source !== window.parent ||
      !isTrustedOrigin(event.origin) ||
      !event.data ||
      typeof event.data !== 'object'
    )
      return
    const message = event.data as { type?: string; data?: unknown }
    if (message.type === 'gm/assets') {
      const payload = message.data as YuzuHostAssetsPayload | undefined
      setAssetUrls(payload?.assetUrls)
      return
    }
    if (message.type === 'gm/init')
      void applyInit((message.data ?? {}) as YuzuHostInitPayload)
  }

  if (import.meta.client) {
    onMounted(() => {
      window.addEventListener('message', handleWindowMessage)
      void announceReady()
    })
    onBeforeUnmount(() =>
      window.removeEventListener('message', handleWindowMessage),
    )
  }

  return { applyInit }
}
